import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Save,
  Lock,
  User,
  Loader2,
  FileText,
  Mail,
  X,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { getAuthToken } from "../lib/api-client";

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
}

export default function Settings() {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [userMessage, setUserMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Organization state (only for legal info)
  const [orgName, setOrgName] = useState("");

  // Legal information state
  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [legalAddress, setLegalAddress] = useState("");
  const [legalCity, setLegalCity] = useState("");
  const [legalPostalCode, setLegalPostalCode] = useState("");
  const [legalProvince, setLegalProvince] = useState("");
  const [legalCountry, setLegalCountry] = useState("España");
  const [legalLoading, setLegalLoading] = useState(false);
  const [legalMessage, setLegalMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Datos legales adicionales: 100% opcionales, solo aplican a algunas
  // actividades (sociedades registradas, actividades con autorización
  // administrativa, profesiones colegiadas). Sección plegada por defecto.
  const [showAdditionalLegalFields, setShowAdditionalLegalFields] =
    useState(false);
  const [legalRegistryEntry, setLegalRegistryEntry] = useState("");
  const [legalAuthorizationNumber, setLegalAuthorizationNumber] =
    useState("");
  const [professionalAssociation, setProfessionalAssociation] = useState("");
  const [professionalLicenseNumber, setProfessionalLicenseNumber] =
    useState("");

  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Email change state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangePassword, setEmailChangePassword] = useState("");
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeMessage, setEmailChangeMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [pendingEmailChange, setPendingEmailChange] = useState<{
    newEmail: string;
    expiresAt: string;
  } | null>(null);

  // Initial data loading
  const [initialLoading, setInitialLoading] = useState(true);

  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const organizationId = localStorage.getItem("rm_current_organization_id");
    const token = getAuthToken();
    return {
      "Content-Type": "application/json",
      ...(organizationId ? { "X-Organization-Id": organizationId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const authFetch = (url: string, options: RequestInit = {}) =>
    fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...(options.headers as Record<string, string>),
      },
      credentials: "include",
    });

  const loadData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [userRes, orgRes] = await Promise.all([
        authFetch(`${API_URL}/users/me`),
        authFetch(`${API_URL}/organizations/me`),
      ]);

      // Handle 401 - redirect to login
      if (userRes.status === 401 || orgRes.status === 401) {
        navigate("/login");
        return;
      }

      if (userRes.ok) {
        const userData = await userRes.json();
        // Check if response has user object or is the user directly
        const user = userData.user || userData;
        setUserProfile(user);
        setFirstName(user.firstName || "");
        setLastName(user.lastName || "");
        setUserPhone(user.phone || "");
      }

      if (orgRes.ok) {
        const orgData = await orgRes.json();
        // Handle response format: { success: true, organization: {...} }
        const org = orgData.organization || orgData;
        setOrgName(org.name || "");
        // Load legal fields
        setLegalName(org.legalName || "");
        setTaxId(org.taxId || "");
        setLegalAddress(org.legalAddress || "");
        setLegalCity(org.legalCity || "");
        setLegalPostalCode(org.legalPostalCode || "");
        setLegalProvince(org.legalProvince || "");
        setLegalCountry(org.legalCountry || "España");
        setLegalRegistryEntry(org.legalRegistryEntry || "");
        setLegalAuthorizationNumber(org.legalAuthorizationNumber || "");
        setProfessionalAssociation(org.professionalAssociation || "");
        setProfessionalLicenseNumber(org.professionalLicenseNumber || "");
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setInitialLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserLoading(true);
    setUserMessage(null);

    try {
      const res = await authFetch(`${API_URL}/users/me`, {
        method: "PUT",
        body: JSON.stringify({ firstName, lastName, phone: userPhone }),
      });

      if (res.ok) {
        const updatedUser = await res.json();
        setUserProfile(updatedUser);
        setUserMessage({
          type: "success",
          text: "Perfil actualizado correctamente",
        });
      } else {
        const error = await res.json();
        setUserMessage({
          type: "error",
          text: error.message || "Error al actualizar perfil",
        });
      }
    } catch (error) {
      setUserMessage({ type: "error", text: "Error de conexión" });
    } finally {
      setUserLoading(false);
    }
  };

  const handleUpdateLegalInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setLegalLoading(true);
    setLegalMessage(null);

    try {
      const res = await authFetch(`${API_URL}/organizations/me`, {
        method: "PUT",
        body: JSON.stringify({
          name: orgName, // Required field
          legalName,
          taxId,
          legalAddress,
          legalCity,
          legalPostalCode,
          legalProvince,
          legalCountry,
          legalRegistryEntry,
          legalAuthorizationNumber,
          professionalAssociation,
          professionalLicenseNumber,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const updatedOrg = data.organization || data;
        // Update legal fields from response
        setLegalName(updatedOrg.legalName || "");
        setTaxId(updatedOrg.taxId || "");
        setLegalAddress(updatedOrg.legalAddress || "");
        setLegalCity(updatedOrg.legalCity || "");
        setLegalPostalCode(updatedOrg.legalPostalCode || "");
        setLegalProvince(updatedOrg.legalProvince || "");
        setLegalCountry(updatedOrg.legalCountry || "España");
        setLegalRegistryEntry(updatedOrg.legalRegistryEntry || "");
        setLegalAuthorizationNumber(updatedOrg.legalAuthorizationNumber || "");
        setProfessionalAssociation(updatedOrg.professionalAssociation || "");
        setProfessionalLicenseNumber(
          updatedOrg.professionalLicenseNumber || "",
        );
        setLegalMessage({
          type: "success",
          text: "Información legal actualizada correctamente",
        });
      } else {
        const error = await res.json();
        setLegalMessage({
          type: "error",
          text: error.message || "Error al actualizar información legal",
        });
      }
    } catch (error) {
      setLegalMessage({ type: "error", text: "Error de conexión" });
    } finally {
      setLegalLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordMessage({
        type: "error",
        text: "Las contraseñas no coinciden",
      });
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({
        type: "error",
        text: "La contraseña debe tener al menos 6 caracteres",
      });
      setPasswordLoading(false);
      return;
    }

    try {
      const res = await authFetch(`${API_URL}/users/me/password`, {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (res.ok) {
        setPasswordMessage({
          type: "success",
          text: "Contraseña actualizada correctamente",
        });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        const error = await res.json();
        setPasswordMessage({
          type: "error",
          text: error.message || "Error al cambiar contraseña",
        });
      }
    } catch (error) {
      setPasswordMessage({ type: "error", text: "Error de conexión" });
    } finally {
      setPasswordLoading(false);
    }
  };

  // ===== Email Change Functions =====

  // Check for pending email change requests
  const checkPendingEmailChange = useCallback(async () => {
    try {
      const res = await authFetch(
        `${API_URL}/users/me/email-change-status`,
        {},
      );
      if (res.ok) {
        const data = await res.json();
        if (data.hasPendingRequest && data.pendingRequest) {
          setPendingEmailChange({
            newEmail: data.pendingRequest.newEmail,
            expiresAt: data.pendingRequest.expiresAt,
          });
        } else {
          setPendingEmailChange(null);
        }
      }
    } catch (error) {
      console.error("Error checking pending email change:", error);
    }
  }, [API_URL]);

  // Load pending email change on mount
  useEffect(() => {
    checkPendingEmailChange();
  }, [checkPendingEmailChange]);

  const handleRequestEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailChangeLoading(true);
    setEmailChangeMessage(null);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailChangeMessage({
        type: "error",
        text: "El formato del email no es válido",
      });
      setEmailChangeLoading(false);
      return;
    }

    try {
      const res = await authFetch(`${API_URL}/users/me/request-email-change`, {
        method: "POST",
        body: JSON.stringify({
          newEmail: newEmail,
          password: emailChangePassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setEmailChangeMessage({
          type: "success",
          text: data.message || "Se ha enviado un email de verificación",
        });
        setNewEmail("");
        setEmailChangePassword("");
        setShowEmailChangeModal(false);
        // Refresh pending status
        checkPendingEmailChange();
      } else {
        setEmailChangeMessage({
          type: "error",
          text: data.message || "Error al solicitar cambio de email",
        });
      }
    } catch (error) {
      setEmailChangeMessage({ type: "error", text: "Error de conexión" });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      const res = await authFetch(`${API_URL}/users/me/cancel-email-change`, {
        method: "DELETE",
      });

      if (res.ok) {
        setPendingEmailChange(null);
        setEmailChangeMessage({
          type: "success",
          text: "Solicitud de cambio de email cancelada",
        });
      } else {
        const data = await res.json();
        setEmailChangeMessage({
          type: "error",
          text: data.message || "Error al cancelar la solicitud",
        });
      }
    } catch (error) {
      setEmailChangeMessage({ type: "error", text: "Error de conexión" });
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <h1 className="text-xl md:text-2xl font-bold text-gray-900">
        Configuración
      </h1>

      {/* User Profile Section */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-base md:text-lg font-semibold text-gray-900">
            Mi Perfil
          </h2>
        </div>

        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apellidos
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={userProfile?.email || ""}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  disabled
                />
                <button
                  type="button"
                  onClick={() => setShowEmailChangeModal(true)}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cambiar
                </button>
              </div>
              {pendingEmailChange ? (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-yellow-800">
                        Pendiente de verificación:{" "}
                        <strong className="break-all">
                          {pendingEmailChange.newEmail}
                        </strong>
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        Revisa tu bandeja de entrada del nuevo correo.
                      </p>
                      <button
                        type="button"
                        onClick={handleCancelEmailChange}
                        className="text-xs text-yellow-700 hover:text-yellow-900 underline mt-1"
                      >
                        Cancelar solicitud
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Puedes cambiar tu email con verificación
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono personal (WhatsApp)
              </label>
              <input
                type="tel"
                value={userPhone}
                onChange={(e) => setUserPhone(e.target.value)}
                placeholder="+34 600 000 000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tu móvil personal, no el del negocio: con él Carmen te
                reconoce cuando escribes al soporte de ReservaManager por
                WhatsApp y te atiende por tu nombre, sin preguntarte quién
                eres. Cada miembro del equipo puede poner el suyo.
              </p>
            </div>
          </div>

          {emailChangeMessage && (
            <div
              className={`p-3 rounded-lg ${emailChangeMessage.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
            >
              {emailChangeMessage.text}
            </div>
          )}

          {userMessage && (
            <div
              className={`p-3 rounded-lg ${userMessage.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
            >
              {userMessage.text}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={userLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {userLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar Perfil
            </button>
          </div>
        </form>
      </div>

      {/* Legal Information Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Información Legal
            </h2>
            <p className="text-sm text-gray-500">
              Datos fiscales para facturas y documentos legales
            </p>
          </div>
        </div>

        <form onSubmit={handleUpdateLegalInfo} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CIF/NIF *
              </label>
              <input
                type="text"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value.toUpperCase())}
                placeholder="B12345678"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Número de identificación fiscal
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razón Social
              </label>
              <input
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Nombre legal de la empresa"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Nombre oficial registrado de la empresa
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dirección Fiscal
            </label>
            <input
              type="text"
              value={legalAddress}
              onChange={(e) => setLegalAddress(e.target.value)}
              placeholder="Calle, número, piso..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ciudad
              </label>
              <input
                type="text"
                value={legalCity}
                onChange={(e) => setLegalCity(e.target.value)}
                placeholder="Ciudad"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código Postal
              </label>
              <input
                type="text"
                value={legalPostalCode}
                onChange={(e) => setLegalPostalCode(e.target.value)}
                placeholder="12345"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provincia
              </label>
              <input
                type="text"
                value={legalProvince}
                onChange={(e) => setLegalProvince(e.target.value)}
                placeholder="Provincia"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              País
            </label>
            <input
              type="text"
              value={legalCountry}
              onChange={(e) => setLegalCountry(e.target.value)}
              placeholder="España"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Datos adicionales: opcionales, plegado por defecto. Solo lo
              necesitan negocios con una forma jurídica/actividad concreta;
              la mayoría de negocios pueden ignorar esta sección entera. */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() =>
                setShowAdditionalLegalFields(!showAdditionalLegalFields)
              }
              aria-expanded={showAdditionalLegalFields}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Datos adicionales (solo si tu actividad lo requiere)
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Opcionales. Solo aplican a sociedades registradas o a
                  actividades reguladas: sanidad, profesiones colegiadas...
                  Si no es tu caso, no hace falta que rellenes nada aquí.
                </p>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                  showAdditionalLegalFields ? "rotate-180" : ""
                }`}
              />
            </button>

            {showAdditionalLegalFields && (
              <div className="p-4 space-y-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datos registrales
                  </label>
                  <input
                    type="text"
                    value={legalRegistryEntry}
                    onChange={(e) => setLegalRegistryEntry(e.target.value)}
                    placeholder="Ej: Registro Mercantil de Madrid, Tomo 12345, Folio 67, Hoja M-123456"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Solo si eres una sociedad inscrita en el Registro
                    Mercantil. Un autónomo no tiene este dato.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Autorización administrativa
                  </label>
                  <input
                    type="text"
                    value={legalAuthorizationNumber}
                    onChange={(e) =>
                      setLegalAuthorizationNumber(e.target.value)
                    }
                    placeholder="Ej: nº de registro sanitario"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Solo actividades que requieren una autorización o registro
                    específico, como el nº de registro sanitario de una
                    clínica. Un restaurante, un taller o una peluquería no lo
                    necesitan.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Colegio profesional
                    </label>
                    <input
                      type="text"
                      value={professionalAssociation}
                      onChange={(e) =>
                        setProfessionalAssociation(e.target.value)
                      }
                      placeholder="Ej: Colegio Oficial de Médicos de Madrid"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Solo profesiones sanitarias o colegiadas: médicos,
                      dentistas, fisioterapeutas, veterinarios, abogados...
                      Un peluquero o un mecánico no se colegian.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº de colegiado
                    </label>
                    <input
                      type="text"
                      value={professionalLicenseNumber}
                      onChange={(e) =>
                        setProfessionalLicenseNumber(e.target.value)
                      }
                      placeholder="Ej: 28/1234567"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Solo si has indicado un colegio profesional arriba.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {legalMessage && (
            <div
              className={`p-3 rounded-lg ${legalMessage.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
            >
              {legalMessage.text}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={legalLoading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {legalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar Información Legal
            </button>
          </div>
        </form>
      </div>

      {/* Password Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <Lock className="w-5 h-5 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Cambiar Contraseña
          </h2>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña Actual
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nueva Contraseña
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar Nueva Contraseña
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={6}
              />
            </div>
          </div>

          {passwordMessage && (
            <div
              className={`p-3 rounded-lg ${passwordMessage.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
            >
              {passwordMessage.text}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={passwordLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passwordLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
              Cambiar Contraseña
            </button>
          </div>
        </form>
      </div>

      {/* Email Change Modal */}
      {showEmailChangeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Cambiar Email
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowEmailChangeModal(false);
                  setNewEmail("");
                  setEmailChangePassword("");
                  setEmailChangeMessage(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleRequestEmailChange} className="p-4 space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Proceso de verificación:</strong>
                </p>
                <ol className="text-xs text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Introduce tu nuevo email y contraseña actual</li>
                  <li>
                    Te enviaremos un enlace de verificación al nuevo email
                  </li>
                  <li>
                    Haz clic en el enlace e introduce tu contraseña para
                    confirmar
                  </li>
                </ol>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email actual
                </label>
                <input
                  type="email"
                  value={userProfile?.email || ""}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nuevo email
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="nuevo@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña actual
                </label>
                <input
                  type="password"
                  value={emailChangePassword}
                  onChange={(e) => setEmailChangePassword(e.target.value)}
                  placeholder="Tu contraseña actual"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Por seguridad, necesitamos verificar tu identidad
                </p>
              </div>

              {emailChangeMessage && (
                <div
                  className={`p-3 rounded-lg ${emailChangeMessage.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
                >
                  {emailChangeMessage.text}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailChangeModal(false);
                    setNewEmail("");
                    setEmailChangePassword("");
                    setEmailChangeMessage(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    emailChangeLoading || !newEmail || !emailChangePassword
                  }
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {emailChangeLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  Enviar verificación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
